// Problem documents (RFC 9457) as the cosmos API sends them; `code` is the platform's error code.

/** One finding of a refused command, as the cosmos API lists them in `problems`. */
export interface ProblemItem {
  code: string;
  message?: string;
  [field: string]: unknown;
}

export interface Problem {
  status: number;
  title: string;
  detail?: string;
  code?: string;
  instance?: string;
  requestId?: string;
  /** The findings behind a refusal (cosmos API); the first one's code stands for the problem. */
  problems?: ProblemItem[];
  [extension: string]: unknown;
}

/** The problem's own code, else the code of its first finding. */
const codeOf = (problem: Problem): string | undefined =>
  problem.code ??
  (Array.isArray(problem.problems) && typeof problem.problems[0]?.code === "string"
    ? problem.problems[0].code
    : undefined);

export function isProblem(value: unknown): value is Problem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Problem).status === "number" &&
    typeof (value as Problem).title === "string"
  );
}

/** Thrown for every non-2xx answer; `code` and `detail` travel unchanged up to the screen. */
export class ProblemError extends Error {
  readonly problem: Problem;
  readonly requestId: string | undefined;

  constructor(problem: Problem, requestId?: string) {
    const code = codeOf(problem);
    super(code ? `${code}: ${problem.detail ?? problem.title}` : problem.title);
    this.name = "ProblemError";
    this.problem = problem;
    this.requestId = requestId ?? problem.requestId;
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string | undefined {
    return codeOf(this.problem);
  }
}
